import React, { useEffect, useRef, useState } from 'react';
import { useEnquiry } from '../context/EnquiryContext';
import { buildWhatsAppLink, site } from '../data/site';
import { IconClose, IconWhatsApp, Lotus } from './ui/Icons';

const DIGITS = /\D/g;

export default function EnquiryModal() {
  const { open, interest, reason, dismiss, markSubmitted } = useEnquiry();

  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  // Lock body scroll + focus the first field while the modal is open.
  useEffect(() => {
    if (!open) return undefined;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => firstFieldRef.current?.focus(), 120);

    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
      if (e.key !== 'Tab') return;

      // Simple focus trap.
      const nodes = dialogRef.current?.querySelectorAll(
        'button, input, textarea, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!nodes?.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, dismiss]);

  if (!open) return null;

  const validate = () => {
    const next = {};
    if (!name.trim() || name.trim().length < 2) next.name = 'Please enter your name.';

    const digits = mobile.replace(DIGITS, '');
    if (digits.length < 10) next.mobile = 'Enter a 10-digit mobile number.';
    else if (digits.length === 10 && !/^[6-9]/.test(digits)) next.mobile = 'That does not look like a mobile number.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSending(true);
    const url = buildWhatsAppLink({
      name: name.trim(),
      mobile: mobile.replace(DIGITS, ''),
      interest,
    });

    // Forward to WhatsApp, then close.
    window.open(url, '_blank', 'noopener,noreferrer');
    markSubmitted();
    setSending(false);
  };

  const headline = reason === 'gate' ? 'Liking what you see?' : 'Send us an enquiry';
  const sub =
    reason === 'gate'
      ? 'Leave your name and number — we will send you prices, weights and what has just come in, straight on WhatsApp.'
      : 'Leave your details and we will continue the conversation on WhatsApp.';

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="enquiry-title"
    >
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close enquiry form"
        onClick={dismiss}
        className="absolute inset-0 h-full w-full cursor-default bg-royal-900/70 backdrop-blur-sm animate-fade-in"
      />

      <div
        ref={dialogRef}
        className="relative w-full max-w-[880px] animate-pop-in overflow-hidden bg-cream shadow-luxe-lg sm:rounded-none"
      >
        <div className="grid md:grid-cols-[0.85fr_1fr]">
          {/* Visual side */}
          <div className="relative hidden min-h-[420px] md:block">
            <img
              src="/assets/gallery/post-15-3.jpg"
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-royal-900/85 via-royal/35 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8">
              <Lotus className="h-5 w-auto text-gold-300" />
              <p className="mt-4 font-display text-[1.7rem] leading-[1.1] text-cream">
                Since 1997,
                <br />
                Ahmedabad
              </p>
              <p className="mt-3 text-[11.5px] uppercase tracking-luxe text-gold-200">
                Antique · Bridal · Diamond
              </p>
            </div>
          </div>

          {/* Form side */}
          <div className="relative p-6 sm:p-9">
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center text-royal/80 transition-colors hover:text-royal"
            >
              <IconClose className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-4">
              <span className="h-px w-8 bg-gold/50" />
              <span className="text-[12px] font-medium uppercase tracking-luxe text-royal/70">Enquire</span>
            </div>

            <h3 id="enquiry-title" className="mt-5 font-display text-[1.8rem] leading-[1.05] text-royal sm:text-[2.2rem]">
              {headline}
            </h3>
            <p className="mt-4 text-[14px] font-light leading-[1.75] text-royal/70">{sub}</p>

            {interest && (
              <p className="mt-5 inline-flex items-center gap-2 border-b border-gold/40 pb-1 text-[11.5px] uppercase tracking-wider2 text-gold-700">
                Interested in {interest}
              </p>
            )}

            <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
              <div>
                <label className="field-label" htmlFor="enq-name">
                  Your name
                </label>
                <input
                  ref={firstFieldRef}
                  id="enq-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  className="field"
                  placeholder="e.g. Kavita Shah"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors((p) => ({ ...p, name: undefined }));
                  }}
                  aria-invalid={!!errors.name}
                />
                {errors.name && <p className="mt-1.5 text-xs text-red-700">{errors.name}</p>}
              </div>

              <div>
                <label className="field-label" htmlFor="enq-mobile">
                  Mobile number
                </label>
                <div className="flex">
                  <span className="flex items-center border border-r-0 border-royal/15 bg-beige/60 px-3 text-sm text-royal/75">
                    +91
                  </span>
                  <input
                    id="enq-mobile"
                    name="mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={11}
                    className="field"
                    placeholder="99044 50916"
                    value={mobile}
                    onChange={(e) => {
                      setMobile(e.target.value);
                      if (errors.mobile) setErrors((p) => ({ ...p, mobile: undefined }));
                    }}
                    aria-invalid={!!errors.mobile}
                  />
                </div>
                {errors.mobile && <p className="mt-1.5 text-xs text-red-700">{errors.mobile}</p>}
              </div>

              <button type="submit" disabled={sending} className="btn-gold w-full disabled:opacity-60">
                <IconWhatsApp solid className="h-4 w-4" />
                {sending ? 'Opening WhatsApp…' : 'Submit & open WhatsApp'}
              </button>

              <p className="text-center text-[13px] font-light leading-relaxed text-royal/70">
                We will open WhatsApp with your details filled in. No spam — just a reply from our
                counter at {site.phoneDisplay}.
              </p>
            </form>

            <button
              type="button"
              onClick={dismiss}
              className="mt-3 w-full text-center text-[13px] uppercase tracking-wider2 text-royal/80 transition-colors hover:text-royal"
            >
              Keep browsing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
